import type {Page} from '@PosterWhiteboard/page/page.class';
import {TextItem} from '@PosterWhiteboard/items/text-item/text-item.class';
import type * as Fabric from '@postermywall/fabricjs-2';
import {Textbox} from '@postermywall/fabricjs-2';
import {checkSpell, getIsSpellCheckEnabledFromStore, isSingleCaseOnlyFont, SQUIGGLY_LINE_ERROR_COLOR, suggestWords, TEXT_HOVER_HIGHLIGHT} from '@Libraries/spell-check-library';
import {RESELECT_ITEM_EVENT_TYPE} from '@PosterWhiteboard/items/item/item.types';
import {getPosterUi} from '@PosterWhiteboard/ui/poster-ui';
import type {SlideshowItem} from '@PosterWhiteboard/items/slideshow-item/slideshow-item.class';
import {closeTextSelectionPopup} from '@Libraries/text-selection-library';
import {isMobile} from 'react-device-detect';
import type {CanvasEvents} from '@postermywall/fabricjs-2';
import {CommonMethods} from '@PosterWhiteboard/common-methods';
import type {TextStyles} from '@PosterWhiteboard/classes/text-styles.class';

interface FirstCharLocation {
  lineIndex: number;
  charIndex: number;
}

interface SpellCheckTarget {
  view: Fabric.Textbox;
  textStyles: TextStyles;
}

export class PageSpellCheck extends CommonMethods {
  public page: Page;

  public constructor(page: Page) {
    super();
    this.page = page;

    this.page.fabricCanvas.on('selection:created', this.onSelectionCreated.bind(this));
    this.page.fabricCanvas.on('selection:updated', this.onSelectionCreated.bind(this));
    this.page.fabricCanvas.on('selection:cleared', this.onSelectionCleared.bind(this));
  }

  public onSelectionCleared(fabricEvent: CanvasEvents['selection:cleared']): void {
    this.page.poster.redux.updateSpellCheckSettingsState(false);
    this.clearMarksOnDeselectedTextboxes(fabricEvent.deselected);
  }

  public async onSelectionCreated(fabricEvent: CanvasEvents['selection:created'] | CanvasEvents['selection:updated']): Promise<void> {
    if (fabricEvent.e?.type === RESELECT_ITEM_EVENT_TYPE) {
      return;
    }
    this.clearMarksOnDeselectedTextboxes('deselected' in fabricEvent ? fabricEvent.deselected : []);
    await this.checkSpellForSelectedObject();
  }

  /**
   * During an in-place edit session the canvas' active object is the textbox, not the item's
   * group, so leaving the item by clicking elsewhere fires `deselected` on the textbox and the
   * group's own deselect handler (which clears its marks) never runs. The squiggles and the
   * misspelled-word highlight stayed on the unselected text. Clearing here from the canvas-level
   * selection events covers the textbox the same way; if the item is re-selected the follow-up
   * check re-marks it.
   */
  private clearMarksOnDeselectedTextboxes(deselected: Fabric.FabricObject[]): void {
    const deselectedTextboxes = deselected.filter((fabricObject): fabricObject is Textbox => fabricObject instanceof Textbox);
    if (deselectedTextboxes.length === 0) {
      return;
    }
    for (const textbox of deselectedTextboxes) {
      this.clearSquigglyLineStyle(textbox);
      this.clearHightlight(textbox);
    }
    this.page.fabricCanvas.requestRenderAll();
  }

  public async checkSpellForSelectedObject(highlightMisspelledWord = false): Promise<void> {
    const fabricTextbox = this.getFabricTextForSpellCheck();
    if (fabricTextbox) {
      await this.checkSpell(fabricTextbox, highlightMisspelledWord);
    }
  }

  public async checkSpell(view: Fabric.Textbox, highlightMisspelledWord = false): Promise<void> {
    const spellCheckEnabled = getIsSpellCheckEnabledFromStore();
    if (view && !spellCheckEnabled) {
      this.clearSquigglyLineStyle(view);
      this.clearHightlight(view);
      this.page.fabricCanvas.requestRenderAll();
    } else if (view && spellCheckEnabled) {
      try {
        this.clearSquigglyLineStyle(view);
        this.clearHightlight(view);
        const words = view.text.split(/[\n -&(-/:-@[-`{-~0-9]/);
        const fontFamily = this.getFontFamilyForSpellCheck();
        const normalizeForSingleCaseFont = fontFamily !== undefined && isSingleCaseOnlyFont(fontFamily);
        const wordsToCheck = words.map((word) => {
          const trimmed = word.trim();
          return normalizeForSingleCaseFont ? trimmed.toUpperCase() : trimmed;
        });
        let index = 0;
        const spellCheckCache = await checkSpell(wordsToCheck);
        for (let i = 0; i < words.length; i++) {
          const word = words[i].trim();
          const isCorrect = spellCheckCache[wordsToCheck[i]];
          if (!isCorrect && word.length) {
            view.squigglylineColor = SQUIGGLY_LINE_ERROR_COLOR;
            view.setSelectionStyles(
              {
                squigglyline: true,
                textBackgroundColor: highlightMisspelledWord ? TEXT_HOVER_HIGHLIGHT : '',
              },
              index,
              index + word.length
            );
            view.dirty = true;
          }
          index += word.length + 1;
        }
        const activeObject = this.getFabricTextForSpellCheck();
        setTimeout(() => {
          if (activeObject === view) {
            if (activeObject.group) {
              activeObject.group.dirty = true;
            }
          } else {
            this.clearSquigglyLineStyle(view);
          }
        }, 500);
        this.page.fabricCanvas.requestRenderAll();
      } catch (e) {
        console.error(e);
      }
    }
  }

  public getFabricTextForSpellCheck(): Fabric.Textbox | undefined {
    return this.getSpellCheckTarget()?.view;
  }

  protected getSpellCheckTarget(): SpellCheckTarget | undefined {
    const itemWithText = this.getItemWithTextForSpellCheck();
    if (!itemWithText) {
      return undefined;
    }
    if (itemWithText.isText()) {
      return {view: itemWithText.fabricTextbox, textStyles: itemWithText.textStyles};
    }
    if (itemWithText.isSlideshow()) {
      const selectedSlide = itemWithText.getSelectedSlide();
      if (selectedSlide.isTextSlide()) {
        return {view: selectedSlide.fabricTextbox, textStyles: selectedSlide.textStyles};
      }
    }
    return undefined;
  }

  public getItemWithTextForSpellCheck(): TextItem | SlideshowItem | undefined {
    const selectedItems = this.page.getSelectedItems();
    if (selectedItems.length === 1 && (selectedItems[0] instanceof TextItem || selectedItems[0].isSlideshow())) {
      return selectedItems[0];
    }
    return undefined;
  }

  protected getFontFamilyForSpellCheck(): string | undefined {
    return this.getSpellCheckTarget()?.textStyles.getFontFamilyToLoad();
  }

  /**
   * The word the caret is in loses its squiggle on every keystroke, so a correction reads as
   * corrected while it is typed; the debounced commit's full check re-marks whatever is still
   * wrong. Also what stops a typed character from inheriting the squiggle of the character
   * before it (fabric's typing inheritance copies the whole style).
   */
  public clearSquigglyLineAroundCaret(view: Fabric.Textbox): void {
    if (!getIsSpellCheckEnabledFromStore() || view.text.length === 0) {
      return;
    }
    const caret = view.selectionStart;
    const wordStart = view.searchWordBoundary(Math.max(caret - 1, 0), -1);
    const wordEnd = view.searchWordBoundary(Math.min(caret, view.text.length), 1);
    if (wordEnd <= wordStart) {
      return;
    }
    view.setSelectionStyles({squigglyline: false, textBackgroundColor: ''}, wordStart, wordEnd);
    view.dirty = true;
    if (view.group) {
      view.group.dirty = true;
    }
  }

  public clearSquigglyLineStyle(clone: Fabric.Textbox): void {
    if (clone) {
      const start = 0;
      const end = clone.text.length;
      clone.setSelectionStyles({squigglyline: false}, start, end);
      if (clone.group) {
        clone.group.dirty = true;
      }
    }
  }

  public clearHightlight(clone: Fabric.Textbox): void {
    if (clone) {
      const start = 0;
      const end = clone.text.length;
      clone.setSelectionStyles({textBackgroundColor: ''}, start, end);
      if (clone.group) {
        clone.group.dirty = true;
      }
    }
  }

  public async handleSuggestionPopupMenu(target: Fabric.Textbox): Promise<void> {
    const start = target.searchWordBoundary(target.selectionStart, -1);
    const end = target.searchWordBoundary(target.selectionStart, 1);
    const text = target.text.substring(start, end).trim();
    const suggestedWords = await suggestWords(text);

    if (this.updatePopupStateNeeded(target)) {
      getPosterUi().updateSpellCheckPopUpTargetAndWords({
        target,
        words: suggestedWords,
        wordPosition: {start, end},
        openSpellCheckPopUpMenu: true,
      });
    } else {
      this.closeSuggestionPopUp();
    }
    if (isMobile && target.selectionStart === target.selectionEnd) {
      closeTextSelectionPopup();
    }
  }

  protected updatePopupStateNeeded(target: Fabric.Textbox): boolean {
    if (isMobile && target.selectionStart !== target.selectionEnd) {
      return false;
    }
    const start = target.searchWordBoundary(target.selectionStart, -1);
    const end = target.searchWordBoundary(target.selectionStart, 1);
    const text = target.text.substring(start, end).trim();
    const hasSquigglyLine = target.getValueOfPropertyAt(this.calcLocation(target).lineIndex, this.calcLocation(target).charIndex, 'squigglyline');

    return hasSquigglyLine && !!text.length;
  }

  protected calcLocation(target: Fabric.Textbox): FirstCharLocation {
    const cursorLocation = target.get2DCursorLocation();
    const {lineIndex} = cursorLocation;
    let charIndex = cursorLocation.charIndex > 0 ? cursorLocation.charIndex - 1 : 0;
    charIndex += target.text.length >= target.selectionStart + 1 && target.searchWordBoundary(target.selectionStart + 1, -1) === target.selectionStart ? 1 : 0;
    return {lineIndex, charIndex};
  }

  public closeSuggestionPopUp(): void {
    getPosterUi().updateSpellCheckPopUpOpenState(false);
  }
}
